import { Awo } from '../../types/Awo';

export type CareType = 'Adopted' | 'Foster' | 'Pre-Surrender';
export type Species = 'Dog' | 'Cat';

export interface Relationship {
  careType: CareType;
  animalType: Species;
  label: string;
}

export interface RelationshipStats {
  careType: string;
  animalType: string;
  supported: number;
  engaged: number;
  lessons: number;
  messages: number;
  surveys: number;
}

const ALL: Relationship[] = [
  { careType: 'Adopted', animalType: 'Dog', label: 'Dog adopters 🐶' },
  { careType: 'Adopted', animalType: 'Cat', label: 'Cat adopters 🐱' },
  { careType: 'Foster', animalType: 'Dog', label: 'Dog fosters 🐶' },
  { careType: 'Foster', animalType: 'Cat', label: 'Cat fosters 🐱' },
  { careType: 'Pre-Surrender', animalType: 'Dog', label: 'Dog pre-surrender 🐶' },
  { careType: 'Pre-Surrender', animalType: 'Cat', label: 'Cat pre-surrender 🐱' },
];

type AwoFlags = Partial<Pick<Awo,
  'id' | 'preSurrenderDashboard' | 'dogAdopt' | 'catAdopt' | 'dogFoster' | 'catFoster' | 'kittenFoster'>>;

// The "By relationship" rows an AWO sees (ticket-2542 AC 7): adopter and foster rows only
// for the programs it runs, and a pre-surrender row for every species it supports. A
// pre-surrender organization supports both species (its settings replace the species flags
// with "This organization is pre-surrender", and its dashboard shows dog and cat cards). An
// admin with no AWO selected sees every row, since the report then covers every AWO.
export function relationshipsFor(awo: AwoFlags): Relationship[] {
  if (!awo.id) return ALL;
  if (awo.preSurrenderDashboard) return ALL.filter((r) => r.careType === 'Pre-Surrender');
  const offers: Record<Exclude<CareType, 'Pre-Surrender'>, Record<Species, boolean>> = {
    Adopted: { Dog: !!awo.dogAdopt, Cat: !!awo.catAdopt },
    Foster: { Dog: !!awo.dogFoster, Cat: !!(awo.catFoster || awo.kittenFoster) },
  };
  const supportsSpecies = (s: Species) => offers.Adopted[s] || offers.Foster[s];
  return ALL.filter((r) => (r.careType === 'Pre-Surrender'
    ? supportsSpecies(r.animalType)
    : offers[r.careType][r.animalType]));
}

// The report only returns relationships with supported pet parents; every shown row still
// renders, with zeros (QA 5.4).
export function statsFor(rows: RelationshipStats[] | undefined, r: Relationship): RelationshipStats {
  return (rows || []).find((s) => s.careType === r.careType && s.animalType === r.animalType) || {
    careType: r.careType, animalType: r.animalType, supported: 0, engaged: 0, lessons: 0, messages: 0, surveys: 0,
  };
}
